import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Chatbot from "./Chatbot";
import AsistanOrb from "./AsistanOrb";
import "./SerdivanAsistan.css";

/**
 * Serdivan+ Asistan (insan avatarı YOK, Siri benzeri dijital "Küre Sahne"):
 *  - preview: sağ altta animasyonlu dijital top
 *  - greeting: top ekran ortasına gelir, titreşir ve karşılama sesini çalar
 *  - chat: karşılamadan sohbete TEK kalıcı orb WAAPI yayıyla süzülür; orb sohbetin
 *    üstünde "hero" olarak durur, cevabın sesine (paylaşılan analyser) göre nefes alır.
 * Tek kalıcı orb instance + tek kalıcı backdrop -> faz geçişinde dikiş/flaş yok.
 */
const ORB_INTRINSIC = 200;
const PHASE_STORE_KEY = "sa-phase"; // oturum boyunca fazı koru (yenileme -> kaldığı yerden)

const SerdivanAsistan = () => {
  // Önceki oturumda sohbetteyse doğrudan chat'e dön (karşılamayı tekrar oynatma).
  const [phase, setPhase] = useState(() => {
    try { return sessionStorage.getItem(PHASE_STORE_KEY) === "chat" ? "chat" : "preview"; }
    catch { return "preview"; }
  }); // preview | greeting | chat
  const [analyser, setAnalyser] = useState(null);
  const [speaking, setSpeaking] = useState(false);

  const orbLayerRef = useRef(null);
  const greetSlotRef = useRef(null);
  const chatSlotRef = useRef(null);
  const firstRectRef = useRef(null);
  const levelRafRef = useRef(0);
  const analyserRef = useRef(null);
  const speakingRef = useRef(false);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  analyserRef.current = analyser;
  speakingRef.current = speaking;

  const reduce = typeof window !== "undefined" && window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const open = () => setPhase("greeting");
  const close = () => setPhase("preview");

  // Fazı oturum deposuna yaz (yalnız kalıcı fazlar; greeting geçicidir -> yazma).
  // Böylece yenileme/gezinmede "chat" ise doğrudan sohbete döner, "preview" ise kapalı kalır.
  useEffect(() => {
    if (phase === "greeting") return;
    try { sessionStorage.setItem(PHASE_STORE_KEY, phase); } catch { /* yok */ }
  }, [phase]);

  // Orb katmanını (fixed) bir yer-tutucu slotun üstüne kilitle.
  const pinOrb = useCallback((rect) => {
    const el = orbLayerRef.current;
    if (!el || !rect || !rect.width) return;
    const s = rect.width / ORB_INTRINSIC;
    el.style.transform =
      `translate(${rect.left + rect.width / 2 - ORB_INTRINSIC / 2}px, ` +
      `${rect.top + rect.height / 2 - ORB_INTRINSIC / 2}px) scale(${s})`;
  }, []);

  // --- Karşılama: sesi çal + orb'u sese bağla ---
  useEffect(() => {
    if (phase !== "greeting") return undefined;
    const audio = new Audio("/asistan/karsilama-ses.mp3");
    audioRef.current = audio;
    let closed = false;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      ctxRef.current = ctx;
      const src = ctx.createMediaElementSource(audio);
      const an = ctx.createAnalyser();
      an.fftSize = 128;
      src.connect(an);
      an.connect(ctx.destination);
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      setAnalyser(an);
    } catch (e) { /* analyser kurulamazsa orb prosedürel titreşir */ }
    setSpeaking(true);

    const goChat = () => {
      if (closed) return;
      closed = true;
      // FIRST ölçüm (hâlâ greeting DOM'undayken): orbun güncel görsel konumu
      if (orbLayerRef.current) firstRectRef.current = orbLayerRef.current.getBoundingClientRect();
      try { audio.pause(); } catch (e) { /* yok */ }
      setSpeaking(false); // analyser'ı null YAPMA -> orb idle nefes alır
      setPhase("chat");
    };
    audio.onended = goChat;
    audio.play().catch(() => {});
    const fallback = setTimeout(goChat, 13000);

    return () => {
      clearTimeout(fallback);
      try { audio.pause(); } catch (e) { /* yok */ }
      try { ctxRef.current && ctxRef.current.close(); } catch (e) { /* yok */ }
      audioRef.current = null;
    };
  }, [phase]);

  // Karşılamaya girişte orb'u greet slotuna kilitle
  useLayoutEffect(() => {
    if (phase !== "greeting") return;
    requestAnimationFrame(() => {
      if (greetSlotRef.current) pinOrb(greetSlotRef.current.getBoundingClientRect());
    });
  }, [phase, pinOrb]);

  // --- greeting -> chat WAAPI yay geçişi (FLIP) ---
  useLayoutEffect(() => {
    if (phase !== "chat") return undefined;
    let ro;
    const rafId = requestAnimationFrame(() => {
      const slot = chatSlotRef.current;
      const layer = orbLayerRef.current;
      if (!slot || !layer) return;
      const lr = slot.getBoundingClientRect();
      const fr = firstRectRef.current;

      // firstRect yoksa (oturumdan doğrudan chat'e dönüş) veya reduced-motion:
      // yay animasyonu değil, orb'u doğrudan hero slotuna pinle.
      if (reduce || !fr || !fr.width) {
        pinOrb(lr);
      } else {
        const gx = fr.left + fr.width / 2, gy = fr.top + fr.height / 2, s0 = fr.width / ORB_INTRINSIC;
        const hx = lr.left + lr.width / 2, hy = lr.top + lr.height / 2, s1 = lr.width / ORB_INTRINSIC;
        const half = ORB_INTRINSIC / 2;
        const arcLift = 44, biasX = (hx - gx) * 0.12;
        const anim = layer.animate([
          { offset: 0.00, transform: `translate(${gx - half}px,${gy - half}px) scale(${s0 * 1.06})` },
          { offset: 0.55, transform: `translate(${(gx + hx) / 2 - half + biasX}px,${(gy + hy) / 2 - half - arcLift}px) scale(${(s0 + s1) / 2 * 0.92})` },
          { offset: 0.86, transform: `translate(${hx - half}px,${hy - half}px) scale(${s1 * 1.06})` },
          { offset: 1.00, transform: `translate(${hx - half}px,${hy - half}px) scale(${s1})` },
        ], { duration: 720, easing: "cubic-bezier(0.22,1,0.36,1)", fill: "both" });
        anim.finished.then(() => {
          if (chatSlotRef.current) pinOrb(chatSlotRef.current.getBoundingClientRect());
        }).catch(() => {});
      }

      // resize / mobil klavye -> orb slota yapışık kalsın (transition'sız re-pin)
      const rePin = () => { if (chatSlotRef.current) pinOrb(chatSlotRef.current.getBoundingClientRect()); };
      ro = new ResizeObserver(rePin);
      ro.observe(slot);
      window.addEventListener("resize", rePin);
      if (window.visualViewport) window.visualViewport.addEventListener("resize", rePin);
      layer._rePin = rePin;
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
      const rePin = orbLayerRef.current && orbLayerRef.current._rePin;
      if (rePin) {
        window.removeEventListener("resize", rePin);
        if (window.visualViewport) window.visualViewport.removeEventListener("resize", rePin);
      }
    };
  }, [phase, reduce, pinOrb]);

  // --- Tek rAF: analyser -> --sa-level / --sa-bar-i / --sa-speaking ---
  useEffect(() => {
    if (phase !== "greeting" && phase !== "chat") return undefined;
    const root = document.documentElement;
    let level = 0;
    let tick = 0;
    const BANDS = 9;
    const loop = () => {
      tick += 1;
      const an = analyserRef.current;
      let lv = 0.05 + 0.03 * Math.sin(tick * 0.045); // nazik idle nefes
      const bars = new Array(BANDS).fill(0);
      if (an && speakingRef.current) {
        try {
          const bins = an.frequencyBinCount;
          const buf = new Uint8Array(bins);
          an.getByteFrequencyData(buf);
          let sum = 0;
          for (let i = 0; i < bins; i++) sum += buf[i];
          lv = sum / bins / 255;
          const per = Math.max(1, Math.floor(bins / BANDS));
          for (let b = 0; b < BANDS; b++) {
            let bs = 0;
            for (let i = 0; i < per; i++) bs += buf[b * per + i] || 0;
            bars[b] = bs / per / 255;
          }
        } catch (e) { /* idle */ }
      }
      level += (lv - level) * 0.35;
      root.style.setProperty("--sa-level", level.toFixed(3));
      root.style.setProperty("--sa-speaking", speakingRef.current ? "1" : "0");
      for (let b = 0; b < BANDS; b++) root.style.setProperty(`--sa-bar-${b}`, bars[b].toFixed(3));
      levelRafRef.current = requestAnimationFrame(loop);
    };
    levelRafRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(levelRafRef.current);
      root.style.setProperty("--sa-level", "0");
      root.style.setProperty("--sa-speaking", "0");
    };
  }, [phase]);

  // Chatbot.speak() -> orb kablolaması
  const handleVoiceState = useCallback(({ speaking: sp, analyser: an }) => {
    setSpeaking(!!sp);
    if (an) setAnalyser(an);
  }, []);

  const inScene = phase === "greeting" || phase === "chat";

  // document.body'ye portal: Swiper slide'ının transform'lu stacking context'inden
  // çıkar -> fixed konum gerçekten viewport'a göre + z-index navbar'ı (9999) geçer.
  return createPortal(
    <>
      {/* --- Önizleme --- */}
      {phase === "preview" && (
        <button className="sa-preview sa-preview-orb" onClick={open} aria-label="Serdivan Asistan'ı aç">
          <span className="sa-preview-ring" />
          <AsistanOrb size={62} speaking={false} />
          <span className="sa-preview-badge">Serdivan Asistan</span>
        </button>
      )}

      {/* --- Kalıcı sahne backdrop (tek dim kaynağı) --- */}
      {inScene && <div className="sa-scene-backdrop" onClick={close} />}

      {/* --- Karşılama katmanı (orb yer-tutucu + metin) --- */}
      {phase === "greeting" && (
        <div className="sa-overlay" onClick={close}>
          <div className="sa-greet" onClick={(e) => e.stopPropagation()}>
            <button className="sa-close" onClick={close} aria-label="Kapat">✕</button>
            <div className="sa-greet-orb-slot" ref={greetSlotRef} />
            <p className="sa-greet-title">Serdivan+ Asistan</p>
            <button className="sa-skip" onClick={() => setPhase("chat")}>Geç →</button>
          </div>
        </div>
      )}

      {/* --- Sesli sohbet --- */}
      {phase === "chat" && (
        <Chatbot
          isOpen={true}
          onClose={close}
          voiceMode={true}
          onVoiceState={handleVoiceState}
          heroSlotRef={chatSlotRef}
        />
      )}

      {/* --- TEK kalıcı orb (greeting + chat boyunca aynı instance, remount YOK) --- */}
      {inScene && (
        <div className="sa-orb-layer" ref={orbLayerRef} aria-hidden="true">
          <AsistanOrb size={ORB_INTRINSIC} speaking={speaking} analyser={analyser} />
        </div>
      )}
    </>,
    document.body
  );
};

export default SerdivanAsistan;
